/**
 * Intakes: those awaiting attorney review first, the rest of the in-flight
 * ones collapsed behind "Show n more". Rows open the intake viewer.
 */
import { Inbox } from "lucide-react";
import { useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

import { MetaLabel } from "@/components/surface";
import { relativeTime } from "@/features/agents/lib/time";
import { cn } from "@/lib/utils";

import { humanizeStatus } from "../../format";
import type { DashboardMode, IntakeRow } from "../../types";
import { EntityLink } from "../EntityLink";
import { CardRow, DashboardCard, ShowMoreFooter } from "./DashboardCard";

type Props = {
  mode: DashboardMode;
  awaitingReview: IntakeRow[] | undefined;
  inFlight?: IntakeRow[];
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onViewAll?: () => void;
  span?: "full";
};

export const IntakesToReviewCard: FC<Props> = ({
  mode,
  awaitingReview,
  inFlight = [],
  isLoading,
  error,
  onRetry,
  onViewAll,
  span
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const review = awaitingReview ?? [];
  const rows = expanded ? [...review, ...inFlight] : review;
  const hidden = inFlight.length;

  return (
    <DashboardCard
      icon={Inbox}
      title="Intakes to Review"
      count={review.length}
      onViewAll={onViewAll}
      isLoading={isLoading && !awaitingReview}
      error={error}
      onRetry={onRetry}
      isEmpty={rows.length === 0}
      emptyTitle="No Intakes Awaiting Review"
      emptyDescription={
        hidden ? `${hidden} in flight with clients.` : undefined
      }
      footer={
        <ShowMoreFooter
          hidden={hidden}
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
          label="in Flight"
        />
      }
      span={span}
      testId="intakes-card"
    >
      {rows.map((row) => (
        <CardRow
          key={row.outgoing_intake_id}
          onClick={() => navigate(row.path)}
          primary={
            <EntityLink path={row.path} refMarkdown={row.ref}>
              {row.title || "Intake"}
            </EntityLink>
          }
          secondary={[
            mode === "practice" ? row.client : null,
            row.updated_at || row.sent_at
              ? relativeTime(row.updated_at ?? row.sent_at ?? "")
              : null
          ]
            .filter(Boolean)
            .join(" · ")}
          trailing={
            <MetaLabel
              className={cn(
                row.status === "awaiting_review" && "font-medium text-primary"
              )}
            >
              {humanizeStatus(row.status)}
            </MetaLabel>
          }
          testId="intake-row"
        />
      ))}
    </DashboardCard>
  );
};
